/**
 * WorkflowArtifactsGallery — shared artifacts list + lightbox for ora and the
 * Outlook plugin.
 *
 * Bundles ``WorkflowArtifactsSection``, ``ArtifactLightbox``, click-to-preview
 * wiring, and optional self-fetch so both surfaces stay in lock-step.
 *
 * Usage:
 *   - Self-fetch panel: pass ``instanceId`` + ``fetchArtifacts``.
 *   - Controlled panel: pass ``artifacts`` (+ optional ``loading``).
 *   - Hoisted lightbox (drawer root): use ``useArtifactLightbox`` and render
 *     ``artifactLightbox`` once at the shell level.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ArtifactLightbox from "./ArtifactLightbox";
import {
  WorkflowArtifactsSection,
  isLightboxViewable,
} from "./WorkflowArtifactsSection";
import type { WorkflowArtifact } from "./WorkflowArtifactsSection";

export type FetchArtifactsFn = (
  instanceId: string,
) => Promise<WorkflowArtifact[]>;

export type FetchArtifactPayloadFn = (
  instanceId: string,
  filename: string,
) => Promise<unknown>;

export interface UseArtifactLightboxOptions {
  artifacts: WorkflowArtifact[];
  instanceId?: string | null;
  fetchArtifactPayload?: FetchArtifactPayloadFn;
}

export interface ArtifactLightboxBindings {
  onArtifactClick: ((index: number) => void) | undefined;
  artifactLightbox: JSX.Element;
}

function useResolvedArtifacts({
  instanceId,
  artifacts: controlledArtifacts,
  loading: controlledLoading,
  fetchArtifacts,
  fetchEnabled = true,
  fetchKey,
}: {
  instanceId?: string | null;
  artifacts?: WorkflowArtifact[];
  loading?: boolean;
  fetchArtifacts?: FetchArtifactsFn;
  fetchEnabled?: boolean;
  fetchKey?: string | null;
}): {
  artifacts: WorkflowArtifact[];
  loading: boolean;
  /** Non-null when the LAST fetch failed. Never inferred from an empty list. */
  error: string | null;
  /** Re-runs the self-fetch. No-op on the controlled path. */
  retry: () => void;
} {
  const isControlled = controlledArtifacts !== undefined;
  const [fetchedArtifacts, setFetchedArtifacts] = useState<WorkflowArtifact[]>(
    [],
  );
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  // Bumped by `retry` so the effect re-runs even though nothing else changed.
  const [attempt, setAttempt] = useState(0);
  const fetchedForRef = useRef<string | null>(null);

  const effectiveKey = fetchKey ?? instanceId ?? null;

  useEffect(() => {
    if (isControlled || !fetchArtifacts || !instanceId || !fetchEnabled) return;
    if (effectiveKey !== null && fetchedForRef.current === effectiveKey) return;
    fetchedForRef.current = effectiveKey;
    setFetchLoading(true);
    setFetchError(null);
    void fetchArtifacts(instanceId)
      .then((items) => {
        setFetchedArtifacts(items);
        setFetchError(null);
      })
      // A transport failure is NOT "this run has no artifacts". Collapsing it
      // into an empty list made a broken backend and a legitimately empty run
      // render identically — no error, no retry, nothing to tell the Servicer
      // the Evidence section is lying to them. The last good list is kept: a
      // failed REFETCH must not erase what is already on screen.
      .catch(() => setFetchError(ARTIFACTS_FETCH_ERROR))
      .finally(() => setFetchLoading(false));
  }, [
    isControlled,
    fetchArtifacts,
    instanceId,
    fetchEnabled,
    effectiveKey,
    attempt,
  ]);

  const retry = useCallback(() => {
    fetchedForRef.current = null;
    setFetchError(null);
    setAttempt((n) => n + 1);
  }, []);

  if (isControlled) {
    return {
      artifacts: controlledArtifacts ?? [],
      loading: controlledLoading ?? false,
      error: null,
      retry,
    };
  }

  return {
    artifacts: fetchedArtifacts,
    loading: fetchLoading,
    error: fetchError,
    retry,
  };
}

/**
 * What the Servicer is told when the artifact fetch fails. Deliberately not
 * the transport's own message: it carries SAS URLs and stack detail, and none
 * of that helps the one decision on offer (try again).
 */
export const ARTIFACTS_FETCH_ERROR =
  "Couldn't load artifacts — try again.";

function ArtifactsFetchError({ onRetry }: { onRetry: () => void }) {
  return (
    <div
      role="alert"
      data-testid="artifacts-section-error"
      className="mt-2 flex items-center gap-2 rounded-md border border-primary-error-300 bg-primary-error-25 px-3 py-2"
    >
      <p className="flex-1 text-xs text-primary-error-700">
        {ARTIFACTS_FETCH_ERROR}
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="shrink-0 text-xs font-semibold text-primary-error-700 hover:underline"
        data-testid="artifacts-section-retry"
      >
        Try again
      </button>
    </div>
  );
}

/** Lightbox state + click handler for controlled artifact arrays. */
export function useArtifactLightbox({
  artifacts,
  instanceId,
  fetchArtifactPayload,
}: UseArtifactLightboxOptions): ArtifactLightboxBindings {
  const viewable = useMemo(
    () => artifacts.filter(isLightboxViewable),
    [artifacts],
  );
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const onArtifactClick = useCallback(
    (index: number) => {
      const clicked = artifacts[index];
      if (!clicked || !isLightboxViewable(clicked)) return;
      const idx = viewable.findIndex((item) => item.url === clicked.url);
      if (idx >= 0) setLightboxIndex(idx);
    },
    [artifacts, viewable],
  );

  const artifactLightbox = (
    <ArtifactLightbox
      open={lightboxIndex !== null}
      artifacts={viewable}
      startIndex={lightboxIndex ?? 0}
      onClose={() => setLightboxIndex(null)}
      instanceId={instanceId}
      fetchArtifactPayload={fetchArtifactPayload}
    />
  );

  return {
    onArtifactClick:
      viewable.length > 0 ? onArtifactClick : undefined,
    artifactLightbox,
  };
}

export interface WorkflowArtifactsGalleryProps {
  instanceId?: string | null;
  /** Controlled artifacts — omit when using ``fetchArtifacts``. */
  artifacts?: WorkflowArtifact[];
  loading?: boolean;
  /** Self-fetch transport (CSV filtering belongs at this seam). */
  fetchArtifacts?: FetchArtifactsFn;
  /** Gate fetch (e.g. drawer accordion expanded). Default true. */
  fetchEnabled?: boolean;
  /** Re-fetch when key changes (plugin phase transitions). */
  fetchKey?: string | null;
  /** Override payload proxy (plugin Product API vs ora Remix route). */
  fetchArtifactPayload?: FetchArtifactPayloadFn;
  variant?: "compact" | "rich" | "strip";
  heading?: string;
}

/** Self-contained artifacts section + lightbox — primary entry point. */
export function WorkflowArtifactsGallery({
  instanceId,
  artifacts: controlledArtifacts,
  loading: controlledLoading,
  fetchArtifacts,
  fetchEnabled = true,
  fetchKey,
  fetchArtifactPayload,
  variant = "compact",
  heading,
}: WorkflowArtifactsGalleryProps) {
  const { artifacts, loading, error, retry } = useResolvedArtifacts({
    instanceId,
    artifacts: controlledArtifacts,
    loading: controlledLoading,
    fetchArtifacts,
    fetchEnabled,
    fetchKey,
  });

  const { onArtifactClick, artifactLightbox } = useArtifactLightbox({
    artifacts,
    instanceId,
    fetchArtifactPayload,
  });

  return (
    <>
      {error ? <ArtifactsFetchError onRetry={retry} /> : null}
      {/* The empty state claims a FACT about the run, so it must not stand in
          for a failed fetch. A stale-but-real list still renders under the
          error — losing what is already on screen helps nobody. */}
      {error && artifacts.length === 0 ? null : (
        <WorkflowArtifactsSection
          artifacts={artifacts}
          loading={loading}
          variant={variant}
          heading={heading}
          onArtifactClick={onArtifactClick}
        />
      )}
      {artifactLightbox}
    </>
  );
}

export default WorkflowArtifactsGallery;
